import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommonProperties } from '../common/common.interfaces';
import { DateTime } from 'luxon';
import { useUserContext } from '../user/UserContext';
import { useLoading } from '../loading/LoadingContext';
import { AuthTableRow } from './interfaces';
import CustomError, { CustomErrorProps } from '../common/components/CustomError';
import { apiFetch } from '../utility/fetch';
import { API_PROD_URL } from '..';
import { Button, Form } from 'react-bootstrap';
import { HomeRootsOAuth2Row } from './components/HomeRootsOAuth2Row';

interface OAuth2MarketplacesProps extends CommonProperties{}

export default function OAuth2Marketplaces(props: OAuth2MarketplacesProps){
  const {userData} = useUserContext()
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const [refresh, setRefresh] = useState<string>(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [error, setError] = useState<CustomErrorProps>({visible: false})
  const [records, setRecords] = useState<AuthTableRow[]>(null)
  const [changes, setChanges] = useState<AuthTableRow[]>([])

  const refreshScreen = () => {
    setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }

  const load = async () => {
    changeStep('Loading Auth Records')
    changeSubStep('Getting records from the auth table.')
    toggleLoading(true)
    const response = await apiFetch<AuthTableRow[]>({
      endpoint: 'azureTables',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      params: [
        {key: 'table', value: 'auth'}
      ]
    })
    if(!response.ok){
      setError({
        title: 'Auth Table Error',
        description: 'Failed to get records from the auth table.',
        content: JSON.stringify(response.body),
        closeError: () => setError({visible: false}),
        visible: true
      })
      toggleLoading(false)
      return
    }
    console.log(response.body)
    setRecords(response.body)
    toggleLoading(false)
  }

  useEffect(() => {
    load()
  }, [refresh])

  const handleRefreshUsingRefreshTokenFlow = useCallback(async (e) => {
    const {pkey, rkey} = e.currentTarget.dataset
    changeStep(`Sending ${pkey.toUpperCase()} ${rkey.toUpperCase()} Request`)
    changeSubStep('Sending the get request to the Crystal Ball API to refresh an access token.')
    toggleLoading(true)
    
    const response = await apiFetch({
      endpoint: 'auth',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      params: [
        {key: 'grant_type', value: 'refresh_token'},
        {key: 'state', value: `${pkey}___${records.find(r => r.partitionKey === pkey && r.rowKey === rkey).refreshCode}`},

      ]
    })
    if(!response.ok){
      setError({
        title: 'Refresh Error',
        description: `Failed to refresh the access token for ${pkey}.`,
        content: JSON.stringify(response.body),
        closeError: () => setError({visible: false}),
        visible: true
      })
      toggleLoading(false)
      return
    }
    refreshScreen()

    toggleLoading(false)
  }, [records])

  const handleRefreshCodeChanged = useCallback((e) => {
    const { pkey, rkey } = e.currentTarget.dataset;
    const value = e.target.value.trim();

    setChanges((prev): AuthTableRow[] => {
      const existingIndex = prev.findIndex(
        c => c.partitionKey === pkey && c.rowKey === rkey
      );

      // Remove if value is blank and record exists
      if (value === '' && existingIndex > -1) {
        return prev.filter((_, idx) => idx !== existingIndex);
      }

      // Update if it exists
      if (value !== '' && existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          refreshCode: value,
        };
        return updated;
      }

      // Add if it doesn't exist and value is non-empty
      if (value !== '') {
        return [
          ...prev,
          {
            partitionKey: pkey,
            rowKey: rkey,
            refreshCode: value,
          },
        ];
      }

      return prev; // No changes
    });
  }, []);

  const handleSaveChangesClicked = useCallback(async (e) => {
    console.log(changes)
    if(changes.length === 0) return
    if(!confirm(`Are you sure you want to update ${changes.length} record(s)?`)) return
    changeStep(`Saving ${changes.length} Records`)
    changeSubStep('Saving records to the auth table.')
    toggleLoading(true)
    const response = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      body: JSON.stringify({
        table: 'auth',
        records: changes
      })
    })
    if(!response.ok){
      setError({
        title: 'Record Save Error',
        description: `Failed to save refresh codes.`,
        content: JSON.stringify({response: response.body, changes}),
        closeError: () => setError({visible: false}),
        visible: true
      })
      toggleLoading(false)
      return
    }
    refreshScreen()
    toggleLoading(false)
  }, [changes])

  const view = useMemo(() => {
    return <>
      <HomeRootsOAuth2Row 
        records={records}
        handleRefreshCodeChanged={handleRefreshCodeChanged}
        handleRefreshUsingRefreshTokenFlow={handleRefreshUsingRefreshTokenFlow}
      />
      {/* ADD MORE HERE! */}
    </>
  }, [records])

  return <div>
    <div id='top'></div>
    <div className='beacon-card'>
      <span className='beacon-header'>OAuth2 Marketplaces</span>
      <br/>
      <span className='beacon-label'>View and refresh access tokens for marketplaces that require a refresh before an expire time.</span>
    </div>
    <CustomError closeError={() => {setError({visible: false})}} content={error?.content} description={error?.description} title={error?.title} visible={error?.visible}></CustomError>
    <Button variant='outline-secondary' onClick={handleSaveChangesClicked}>SAVE CHANGES</Button>
    <table className='flexport-table'>
      <thead>
        <tr className="sticky-row-2">
          <th>MARKETPLACE</th>
          <th>EXPIRES ON</th>
          <th>ACTIONS</th>
        </tr>
      </thead>
      <tbody>
        {view}
      </tbody>
    </table>
    
  </div>
}
